import { consume } from '@lit/context';
import { t } from 'i18next';
import { css, html, LitElement, nothing, PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';

import {
  iconCheckmark,
  iconPencil,
  iconTrashCan,
  iconUpload,
} from '@/components/icons';
import {
  global,
  GlobalComponentType,
  personalisation,
  PersonalisationType,
} from '@/context';
import { BREAKPOINT_SMALL_PX } from '@/settings';
import { baseStyles, buttonStyles, typographyStyles } from '@/styles';
import { ActionButton, PersonalisationTypes } from '@/types';

@customElement('fg-personalisation-status-bar')
export class FGPersonalisationStatusBar extends LitElement {
  @property({ type: String })
  selectedPrintOptionCode: string = '';

  @property({ type: Object })
  personalisationData?: PersonalisationType;

  @property({ type: String })
  printOptionName?: string;

  @property({ type: Boolean })
  showActions = true;

  @property({ type: Boolean })
  editOnProductPage = false;

  @state()
  hasPersonalisation = false;

  @consume({ context: global.context })
  globalContext!: GlobalComponentType;

  openPersonalisationModal = () => {
    this.dispatchEvent(new CustomEvent('open'));
  };

  editPersonalisation = () => {
    if (!this.personalisationData?.visualDesignId) {
      throw new Error('Visual design ID is required');
    }

    if (this.editOnProductPage) {
      this.dispatchEvent(new Event('configure', { composed: true }));
      return;
    }

    this.dispatchEvent(
      new CustomEvent('open', {
        composed: true,
        detail: {
          option: {
            visualDesignId: this.personalisationData.visualDesignId,
          },
        },
      }),
    );
  };

  removePersonalisation = () => {
    this.dispatchEvent(new CustomEvent('remove'));
  };

  protected willUpdate(_changedProperties: PropertyValues) {
    if (_changedProperties.has('personalisationData')) {
      const { logoUuid, textPersonalization, visualDesignId } =
        this.personalisationData || {};
      if (logoUuid || textPersonalization || visualDesignId) {
        this.hasPersonalisation = true;
        return;
      }
      this.hasPersonalisation = false;
    }
  }

  handleRetailPersonalisationData = (
    personalisationData?: PersonalisationType,
  ) => {
    if (
      !this.hasPersonalisation ||
      !this.selectedPrintOptionCode ||
      !personalisationData
    ) {
      return html`
        <span>${t('labels.uploadLogoTextAfterOrder')}</span>
      `;
    }

    const { type, textPersonalization, uploadedFileName, visualDesignId } =
      personalisationData;

    if (visualDesignId) {
      return html`
        <span>
          ${iconCheckmark}
          ${visualDesignId
            ? t('labels.personalisationMadeInVisualDesigner')
            : nothing}
        </span>
      `;
    }

    const personalisationTypes: PersonalisationTypes = {
      text: {
        description: textPersonalization?.text,
        fontFamily: textPersonalization?.font,
      },
      image: {
        logo: uploadedFileName || undefined,
      },
      text_image: {
        logo: uploadedFileName || undefined,
        description: textPersonalization?.text,
        fontFamily: textPersonalization?.font,
      },
    };

    if (type && this.hasPersonalisation && this.selectedPrintOptionCode) {
      const { logo, description, fontFamily } = personalisationTypes[type];
      return html`
        <p>
          <span>
            ${logo
              ? html`
                  ${logo}
                `
              : nothing}
            ${description
              ? html`
                  ${logo ? '-' : nothing}
                  <span style=${fontFamily ? `font-family:${fontFamily}` : ''}>
                    ${description}
                  </span>
                `
              : nothing}
          </span>

          <button
            class="button-link"
            @click=${() =>
              personalisation.providerRemoveEvent(
                this,
                this.selectedPrintOptionCode,
              )}
            title="${t('actions.deleteLogoAndOrText') || ''}"
          >
            <span aria-hidden="true" class="delete-text">
              ${t('actions.delete')}
            </span>
            <fg-collapsible-icon-button
              actionButton=${ActionButton.DELETE}
              asDiv
            ></fg-collapsible-icon-button>
          </button>
        </p>
      `;
    }
  };

  handleWholesalePersonalisationData = (
    personalisationData?: PersonalisationType,
  ) => {
    if (!this.hasPersonalisation || !personalisationData) {
      return html`
        <span>${t('labels.logoOrTextNotUploaded')}</span>
      `;
    }
    const { textPersonalization, uploadedFileName, logoUuid, visualDesignId } =
      personalisationData;

    return html`
      <span>
        ${iconCheckmark}
        ${uploadedFileName && logoUuid ? uploadedFileName : nothing}
        ${uploadedFileName && textPersonalization ? '-' : nothing}
        ${textPersonalization ? `"${textPersonalization.text}"` : nothing}
        ${visualDesignId
          ? t('labels.personalisationMadeInVisualDesigner')
          : nothing}
      </span>
    `;
  };

  handleEditButton = (personalisationData?: PersonalisationType) => {
    if (personalisationData?.visualDesignId) {
      return html`
        <button class="edit" @click=${this.editPersonalisation}>
          ${t('actions.edit')}
          <span class="icon-wrapper" aria-hidden="true">${iconPencil}</span>
        </button>
      `;
    }
    return nothing;
  };

  render() {
    const { layout } = this.globalContext;
    const { visualDesignId } = this.personalisationData || {};

    return html`
      <div
        class="personalisation-status-bar ${classMap({
          active: this.hasPersonalisation,
          retail: layout === 'retail',
        })}"
      >
        ${layout === 'retail'
          ? this.handleRetailPersonalisationData(this.personalisationData)
          : this.handleWholesalePersonalisationData(this.personalisationData)}
        ${this.showActions
          ? (() => {
              if (!this.hasPersonalisation) {
                return layout === 'retail'
                  ? html`
                      <button
                        class="button button-primary button-small button-icon"
                        title=${t('labels.submitLogoAndTextNow') || ''}
                        @click=${this.openPersonalisationModal}
                      >
                        ${t('actions.uploadDirectly')}
                        <span aria-hidden="true">${iconUpload}</span>
                      </button>
                    `
                  : html`
                      <button
                        class="button button-secondary"
                        @click=${this.openPersonalisationModal}
                      >
                        ${t('actions.uploadLogoText')}
                      </button>
                    `;
              }

              if (layout === 'wholesale' || visualDesignId) {
                return html`
                  <span class="buttons-wrapper">
                    ${this.handleEditButton(this.personalisationData)}
                    <button class="remove" @click=${this.removePersonalisation}>
                      <span class="icon-wrapper" aria-hidden="true">
                        ${iconTrashCan}
                      </span>
                      <span class="sr-only">
                        ${t('labels.removePrintOptionPersonalisation', {
                          name: this.printOptionName,
                        })}
                      </span>
                    </button>
                  </span>
                `;
              }

              return nothing;
            })()
          : nothing}
      </div>
    `;
  }

  static styles = [
    baseStyles,
    typographyStyles,
    buttonStyles,
    css`
      .personalisation-status-bar {
        display: flex;
        justify-content: space-between;
        align-items: center;
        min-height: 64px;
        background-color: #ffffff;
        padding: 0.75rem;
        border: 1px solid var(--fg-gray--300, var(--fg-fallback-gray--300));
        gap: 0.5rem;
        border-radius: var(
          ---fg---fg-borderradius--xs,
          var(--fg-fallback-borderradius--xs)
        );

        &.active {
          border: 1px solid var(--fg-success, var(--fg-fallback-success));

          & > span {
            color: var(--fg-success--dark, var(--fg-fallback-success-dark));
          }
        }

        & > span {
          font-style: italic;
        }
      }

      :host([part='retail']) .personalisation-status-bar {
        align-items: center;
        background-color: var(--fg-gray--100, var(--fg-fallback-gray--100));
        border: none;
        border-radius: var(--fg-borderradius, var(--fg-fallback-borderradius));
        flex-wrap: wrap;
        margin: 8px 0 16px;
        min-height: inherit;
        padding: 12px;
      }

      .buttons-wrapper {
        display: flex;
        gap: 0.5rem;
        align-items: end;
      }

      span {
        color: var(--fg-gray--800, var(--fg-fallback-gray--800));
        font-size: 1rem;
      }

      .button-link {
        display: inline-flex;
        gap: 8px;
        margin-left: 4px;
      }

      .button-small {
        display: block;
        margin-top: 8px;
        width: 100%;
        @media screen and (min-width: ${BREAKPOINT_SMALL_PX}px) {
          display: inline-flex;
          margin-top: 0;
          width: auto;
        }
      }

      .button-small span {
        color: white;
      }

      .edit {
        align-items: center;
        color: var(--fg-primary, var(--fg-fallback-primary));
        display: flex;
        font-style: normal;
        gap: 0.5rem;
        span.icon-wrapper {
          display: grid;
          background-color: var(--fg-primary, var(--fg-fallback-primary));
          border-radius: 999px;
          padding: 5px;
          svg {
            width: 12px;
            height: 12px;
            color: #ffffff;
          }
        }
      }

      :host([part='wholesale']) .edit {
        color: var(--fg-secondary, var(--fg-fallback-secondary));
        span.icon-wrapper {
          background-color: var(--fg-secondary, var(--fg-fallback-secondary));
        }
      }

      .remove {
        span.icon-wrapper {
          display: grid;
          border: 1px solid var(--fg-gray--300, var(--fg-fallback-gray--300));
          border-radius: 999px;
          padding: 5px;
          svg {
            width: 12px;
            height: 12px;
            color: var(--fg-red--500, var(--fg-fallback-red--500));
          }
        }
      }
    `,
  ];
}
